import { onMounted, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import { flatMap, debounce } from "lodash";

export declare type LayerLegend = {
  id: string;
  legends: Legend[];
  name: string;
}

export declare type Legend = {
  title: string;
  color?: string;
  icon?: string;
  style?: string;
};

export default function useLayerLegend(vizUrl: string) {
  const store = useStore();
  const legends = ref<LayerLegend[]>([]);
  const loading = ref(false);
  let stopWatch;
  let stopWatchLeaflet;

  const fetchLegends = async (bbox) => {
    loading.value = true;
    if (!vizUrl) return;
    try {
      let url = vizUrl;
      if (bbox) {
        url += `?bbox_e=${bbox.east}&bbox_n=${bbox.north}&bbox_s=${bbox.south}&bbox_w=${bbox.west}`;
      }
      const response = await axios.get(url);
      if (response.data && response.data.layers) {
        const layersWithLegends = response.data.layers.filter(
          (layer) => layer.legends && layer.legends.length > 0 && layer.legends[0].definition?.categories
        );

        const l = layersWithLegends.map((layer) => {
          return {
            id: layer.id,
            name: layer.options.layer_name,
            legends: layer.legends[0].definition.categories,
          }
        });
        legends.value = [];
        legends.value = flatMap<LayerLegend>(l);
      }
      loading.value = false;
    } catch (error) {
      console.error("Error fetching legends:", error);
    }
  };

  const debouncedFetchLegends = debounce(fetchLegends, 1000);

  onMounted(() => {
    debouncedFetchLegends(null);
    stopWatch = watch(
      () => store.state.engineStore.bbox,
      async (newVal) => {
        await debouncedFetchLegends(newVal);
      }
    );
    stopWatchLeaflet = watch(
      () => store.state.exportStore.mapData.bbox,
      async (newVal) => {
        await debouncedFetchLegends(newVal);
      }
    )
  });

  onUnmounted(() => {
    if (stopWatch) {
      stopWatch(); 
      stopWatch = null;
    }
    if (stopWatchLeaflet) {
      stopWatchLeaflet();
      stopWatchLeaflet = null;
    }
  })

  return {
    legends,
  };
}
